import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, TemplateRef, inject } from '@angular/core'
import { expandCollapse, openClose } from 'src/app/shared/animations/open-close-animations'
import { Dictionary } from '@ngrx/entity'
import { IconNamesEnum, NgxBootstrapIconsModule } from 'ngx-bootstrap-icons'
import { PhoneFormatPipe } from '../../pipes/phone-format.pipe'
import { LinkedInComponent } from '../linked-in/linked-in.component'
import { NgTemplateOutlet, DatePipe, DecimalPipe } from '@angular/common'
import { NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbPopover } from '@ng-bootstrap/ng-bootstrap'
import { FormsModule } from '@angular/forms'
import { StripTagsPipe } from '../../pipes/striptags.pipe'

export interface FilterableTableColumn {
  name: string
  title: string
  type: string
  sortable: boolean
  filterable: boolean
  filterArrayBy?: string
  sorted?: string
  sortby?: string
  sortbyType?: string
  routerLink?: string
  filters?: {
    value: string
    checked: boolean
  }[],
}

@Component({
    animations: [
        openClose,
        expandCollapse
    ],
    selector: 'auto-filter-table',
    templateUrl: './auto-filter-table.component.html',
    styleUrls: ['./auto-filter-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        NgbDropdown,
        NgxBootstrapIconsModule,
        NgbDropdownToggle,
        NgbDropdownMenu,
        NgbPopover,
        NgTemplateOutlet,
        LinkedInComponent,
        DatePipe,
        PhoneFormatPipe,
        DecimalPipe,
        StripTagsPipe
    ]
})

export class AutoFilterTableComponent implements OnChanges {

  @Input() data: Dictionary<any>[] = []
  @Input() columns: FilterableTableColumn[] = []
  @Input() canExpand: boolean = false
  @Input() expansionTemplate!: TemplateRef<any>
  @Input() showAll = false
  @Input() valueTemplates: Dictionary<TemplateRef<any>> = {}
  @Input() set searchString(value: string) {
    this.filterText = value
  }
  @Input() hideSearch: boolean = false

  public iconNames = IconNamesEnum

  public filteredTableData: Dictionary<any>[] = []
  public filteredTableDataLength = 0

  private currentColumnForSorting: FilterableTableColumn | null = null
  public filters: Dictionary<any> = {}
  public filterText = ''
  public selectMenuOptions: string[] = []
  public selectedRange: string = '1 - 10'
  public start = 0
  public end = 9

  public isExpanded: Dictionary<boolean> = {}

  private changeDetector = inject(ChangeDetectorRef)

  getNamedColumn(named: string): any[] {
    let result: any[] = []
    this.data.forEach((row: any) => result.push(row[named]))
    return result
  }

  removeEmptyColumns(columns: FilterableTableColumn[], data: Dictionary<any>[]): FilterableTableColumn[] {
    let emptyColumnNames: string[] = columns.map(column => column.name)
    for (const row of data) {
      for (const name of structuredClone(emptyColumnNames)) {
        const value = row[name]
        if (value !== undefined && ((typeof value === 'string' && value.length > 0) || (typeof value === 'object' && value !== undefined))) {
          emptyColumnNames.splice(emptyColumnNames.indexOf(name), 1)
        }
      }
      if (emptyColumnNames.length === 0) {
        break
      }
    }
    if (emptyColumnNames.length === 0) {
      return columns
    } else {
      return columns.filter(column => !emptyColumnNames.includes(column.name))
    }
  }

  calculateSelectMenuOptions() {
    this.selectMenuOptions = []
    for (let i = 0; i < Math.floor(this.filteredTableDataLength / 10); i++) {
      this.selectMenuOptions.push( `${i * 10 + 1} - ${(i + 1) * 10}`)
    }
    if (this.filteredTableDataLength % 10 > 0) {
      this.selectMenuOptions.push(`${Math.floor(this.filteredTableDataLength / 10) * 10 + 1} - ${this.filteredTableDataLength}`)
    }
    if (this.showAll) {
      this.start = 0
      this.end = this.filteredTableDataLength
      this.selectedRange = 'All'
    } else {
      this.start = 0
      this.end = Math.min(this.filteredTableDataLength - 1, 9)
      this.selectedRange = `1 - ${Math.min(10, this.filteredTableDataLength)}`
    }
  }

  changePage() {
    if (this.selectedRange === 'All') {
      this.start = 0
      this.end = this.filteredTableDataLength
    } else {
      const range = this.selectedRange.split(' - ')
      this.start = parseInt(range[0]) - 1
      this.end = parseInt(range[1]) - 1
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.columns = this.removeEmptyColumns(structuredClone(this.columns), this.data)

    for (let index = 0; index < this.columns.length; index++) {
      let column = this.columns[index]
      if (column.sortable) {
        column.sorted = column.sorted ?? ''
        if (column.sorted.length) {
          this.currentColumnForSorting = column
        }
      }
      if (column.filterable) {
        let distinctColumnValues = [... new Set(this.getNamedColumn(column.name))]
        column.filters = []
        if (distinctColumnValues.length > 1) {
          column.filters = distinctColumnValues.map(value => {
            return {
              value,
              checked: true
            }
          }).filter(value => value.value?.length > 0)
          column.filters.sort( (a, b) => a.value.localeCompare(b.value))
          this.filters[column.name] = column
        } else {
          column.filterable = false
        }
      }
    }
    this.filteredTableDataLength = this.data.length
    this.filteredTableData = structuredClone(this.data)

    if (this.filterText.length > 0) {
      this.filterTableData()
    } else {
      this.calculateSelectMenuOptions()
    }
    for (let i = 0 ; i < this.filteredTableDataLength; i++) {
      this.isExpanded[i] = this.filteredTableData[i]['expanded'] ?? false
    }
  }

  filterTableData(clearFilterText = false) {
    if (clearFilterText) {
      this.filterText = ''
    }
    this.filteredTableDataLength = 0
    this.filteredTableData = []
    this.data.forEach((row: any) => {
      if (row.expanded) {
        this.filteredTableData.push(row)
        this.filteredTableDataLength++
        return
      }

      let visible = true
      let textMatch = this.filterText.length === 0
      for (const [columnName, column] of Object.entries(this.filters)) {
        const value = row[columnName]
        column.filters.forEach((filter: any) => {
          if (filter.value === value) {
            visible = visible && filter.checked
          }
        })
      }
      if (visible && this.filterText.length > 0) {
        for (const column of this.columns) {
          if (column.sortby && row[column.sortby]) {
            textMatch = textMatch || row[column.sortby]?.toLowerCase().includes(this.filterText?.toLowerCase())
          } else {
            if (Array.isArray(row[column.name])) {
              if (column.filterArrayBy) {
                row[column.name].forEach((value: any) => {
                  value.hidden = !value[column.filterArrayBy!]?.toLowerCase().includes(this.filterText?.toLowerCase())
                  textMatch = textMatch || !value.hidden
                })
              }

            } else {
              if (row[column.name] && row[column.name] !== "") {
                textMatch = textMatch || row[column.name]?.toLowerCase().includes(this.filterText?.toLowerCase())
              }
            }
          }
          if (textMatch) {
            break
          }
        }
      }

      if (visible && textMatch) {
        this.filteredTableData.push(row)
        this.filteredTableDataLength++
      }
    })
    this.calculateSelectMenuOptions()
    for (let i = 0 ; i < this.filteredTableDataLength; i++) {
      this.isExpanded[i] = this.filteredTableData[i]['expanded'] ?? false
    }
  }

  toggleOrder(column: FilterableTableColumn) {
    if (column.sorted === '') {
      if (this.currentColumnForSorting) {
        this.currentColumnForSorting.sorted = ''
      }
      column.sorted = 'asc'
      this.currentColumnForSorting = column
    } else if (column.sorted === 'asc') {
      column.sorted = 'desc'
    } else {
      column.sorted = 'asc'
    }

    let columnToSortBy = column.sortby ?? column.name

    this.filteredTableData.sort((a: any, b: any) => {
      const sortbyType = column.sortbyType ?? column.type
      if (column.sorted === 'asc') {
        if (sortbyType === 'number') {
          return a[columnToSortBy] - b[columnToSortBy]
        } else {
          return (a[columnToSortBy] ?? '').localeCompare(b[columnToSortBy] ?? '', undefined, { sensitivity: 'base' })
        }
      } else {
        if (sortbyType === 'number') {
          return b[columnToSortBy] - a[columnToSortBy]
        } else {
          return (b[columnToSortBy] ?? '').localeCompare(a[columnToSortBy] ?? '', undefined, { sensitivity: 'base' })
        }
      }
    })
    this.calculateSelectMenuOptions()
  }

  thereAreCheckedFilters(filters: any[]): boolean {
    return filters.filter(filter => filter.checked).length < filters.length
  }

  getLinkedInHandle(str: string): string {
    try {
      const url = new URL(str)
      return url.pathname.replace('/in/', '').replace('/', '')
    } catch (e) {
      return ''
    }
  }
  trimURL(url: string | undefined) {
    let result = ''
    if (url !== undefined) {
      result = url.split('#')[0]
      result = result.split('?')[0]
    }
    return result
  }

}